import { observer } from "mobx-react-lite";
import { useMemo } from "react";

import { useTran } from "@/hooks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { INDUSTRIAL_CLASSIFICATION } from "@/lib/constants";
import { useOrganizationStore } from "../hooks";
import { getUnitTypeMeta } from "../utils/organization.helpers";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const DetailRow = ({ label, value }) => (
  <div className="space-y-1 rounded-radius border border-border p-3 text-sm">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">
      {label}
    </p>
    <p className="text-sm font-medium text-foreground">{value ?? "—"}</p>
  </div>
);

const Breadcrumbs = ({ trail = [], activeColor }) => {
  if (!trail.length) return null;
  const lastIndex = trail.length - 1;

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      {trail.map((crumb, index) => {
        const isLast = index === lastIndex;

        return (
          <span key={crumb.id} className="flex items-center gap-2">
            <span
              className={`px-2 py-1 text-[11px] ${
                isLast ? activeColor : "text-muted-foreground"
              }`}
            >
              {crumb.name}
            </span>

            {!isLast && <span className="text-muted-foreground">››</span>}
          </span>
        );
      })}
    </div>
  );
};

const OrganizationDetails = observer(({ onEdit, onDelete }) => {
  const { t } = useTran();
  const { selectedUnit, loading } = useOrganizationStore();
  const descriptionKey = selectedUnit?.description;
  const industryLabel = useMemo(() => {
    if (!descriptionKey) return t("Not set");
    return (
      INDUSTRIAL_CLASSIFICATION.find((c) => c.key === descriptionKey)?.value ||
      descriptionKey
    );
  }, [descriptionKey, t]);

  if (loading && !selectedUnit) {
    return (
      <Card className="h-full">
        <CardHeader className="space-y-2">
          <div className="h-5 w-1/3 animate-pulse rounded-full bg-muted" />
          <div className="h-4 w-1/2 animate-pulse rounded-full bg-muted/70" />
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div
              key={idx}
              className="h-16 animate-pulse rounded-radius bg-muted/60"
            />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!selectedUnit) {
    return (
      <Card className="flex h-full flex-col items-center justify-center border-dashed text-center">
        <p className="text-base font-semibold text-foreground">
          {t("Select a unit to view details")}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t(
            "Highlight a node from the tree to inspect metadata, region, and hierarchy.",
          )}
        </p>
      </Card>
    );
  }

  const meta = getUnitTypeMeta(selectedUnit.unitLevel);
  const parentCrumb =
    selectedUnit.breadcrumbs && selectedUnit.breadcrumbs.length > 1
      ? selectedUnit.breadcrumbs[selectedUnit.breadcrumbs.length - 2]
      : null;

  return (
    <Card className="h-full border-border py-2">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex h-9 w-9 items-center justify-center rounded-full bg-background shadow-sm ${meta.color}`}
              >
                <meta.icon className="h-4 w-4" />
              </span>
              <CardTitle className="text-base text-foreground flex flex-wrap gap-2">
                {selectedUnit.name}
                <Badge
                  className={`${meta.color} bg-transparent rounded-full border border-border text-xs`}
                >
                  {t(meta.label)}
                </Badge>
              </CardTitle>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={selectedUnit.unitLevel === "parent_company"}
              onClick={() => onEdit?.(selectedUnit)}
            >
              {t("Edit")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={selectedUnit.unitLevel === "parent_company"}
              onClick={() => onDelete?.(selectedUnit)}
            >
              {t("Delete")}
            </Button>
          </div>
        </div>
        <Breadcrumbs
          trail={selectedUnit.breadcrumbs}
          activeColor={meta.color}
        />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <DetailRow label={t("Unit Level")} value={t(meta.label)} />
          <DetailRow
            label={t("Status")}
            value={selectedUnit.isActive ? t("Active") : t("Inactive")}
          />
          <DetailRow label={t("Parent")} value={parentCrumb?.name || "—"} />
          <DetailRow
            label={t("Country")}
            value={selectedUnit.countryLabel || selectedUnit.country || "—"}
          />
          <DetailRow
            label={t("Unit Type")}
            value={selectedUnit.unitType || "—"}
          />
        </div>

        {selectedUnit.description ? (
          <div className="rounded-radius border border-border bg-background p-3 text-sm text-foreground">
            {industryLabel}
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
          <p>
            {t("Last updated")}:{" "}
            <span className="font-medium text-foreground">
              {formatDate(selectedUnit.updatedAt)}
            </span>
          </p>
          <p>
            {t("Created")}:{" "}
            <span className="font-medium text-foreground">
              {formatDate(selectedUnit.createdAt)}
            </span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
});

export default OrganizationDetails;
